import React, { useCallback } from 'react';
import orderBy from 'lodash/orderBy';

// Helpers
import { sanitizeFullName } from 'Shared/helpers';

// Components
import WpSelect from 'Wpds/components/select/WpSelectV2';
import WpAvatarGroup from 'Wpds/components/avatar/WpAvatarGroup';

// Styles
import './WorkspaceSelectInput.scss';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  /** ID for the input */
  id: string;
  /** Label for the input */
  label?: string;
  /** Hide the label, but keep on screen for screen readers */
  labelHidden?: boolean;
  /** Hint text to display */
  placeholder?: string;
  /** Initial value for the input */
  value?: string;
  required?: boolean;
  /** Error to display beneath the label */
  error?: string;
  workspaces?: Array<any>;
  onChange?: Function;
  searchPlaceholder?: string;
  showSearch?: boolean;
  disabled?: boolean;
  showRequiredIndicator?: boolean;
};

const WorkspaceSelect = ({
  workspaces = [],
  id,
  label,
  onChange,
  error,
  required = false,
  placeholder,
  labelHidden,
  showSearch = true,
  disabled = false,
  value,
  searchPlaceholder,
  showRequiredIndicator,
}: Props) => {
  const { tr } = useTranslation();
  const resolvedLabel = label ?? tr('perform.workspace.select.choose_workspace_label');
  const resolvedSearchPlaceholder = searchPlaceholder ?? tr('perform.workspace.select.search_workspaces_placeholder');

  const baseClassName = 'WorkspaceSelectInput';
  const { id: currentUserId } = useCurrentUser();
  // Add the label and value expected for the input
  const formattedOptions = [
    ...workspaces.map(workspace => {
      return {
        ...workspace,
        label: workspace.name,
        value: workspace.id,
      };
    }),
  ];

  const sortedOptions = () => {
    const orderWorkspacesAlphabetically = list => {
      return orderBy(list, ({ label }) => label);
    };

    // Get the appropriate workspaces broken out by type
    const personalWorkspace = formattedOptions.filter(({ isPersonal }) => isPersonal);
    let oneOnOneWorkspaces = formattedOptions.filter(({ isOneOnOne }) => isOneOnOne);
    const teamSyncWorkspace = formattedOptions.filter(({ isTeamSync }) => isTeamSync);
    let groupWorkspaces = formattedOptions.filter(({ isGroup, isTeamSync }) => isGroup && !isTeamSync);

    // Need to clean up name of 1:1's created by someone else other than the current user
    oneOnOneWorkspaces = oneOnOneWorkspaces.map(oneOnOne => {
      const workspaceName = oneOnOne.name;
      const restUser = oneOnOne.users?.find(({ id: userId }) => userId !== currentUserId);
      const name = restUser
        ? sanitizeFullName(restUser.fullName) || sanitizeFullName(workspaceName) || restUser.email
        : sanitizeFullName(workspaceName);
      return { ...oneOnOne, label: name };
    });

    // Order names alphabetically
    oneOnOneWorkspaces = orderWorkspacesAlphabetically(oneOnOneWorkspaces);
    groupWorkspaces = orderWorkspacesAlphabetically(groupWorkspaces);

    return [...personalWorkspace, ...oneOnOneWorkspaces, ...teamSyncWorkspace, ...groupWorkspaces];
  };

  const handhandleWorkspaceSelectClick = useCallback(
    selected => {
      if (onChange) onChange(selected);
    },
    [onChange]
  );

  const CustomWorkspaceOption = ({ option }) => {
    return (
      <div className={`${baseClassName}__select-options`}>
        <span className={`${baseClassName}__select-options__label`}>{option.label}</span>
        <WpAvatarGroup users={option.users} stacked size="sm" showAdditionalCount />
      </div>
    );
  };

  return (
    <WpSelect
      id={id}
      label={resolvedLabel}
      placeholder={placeholder}
      labelHidden={labelHidden}
      isSearchable={showSearch}
      options={sortedOptions()}
      error={error}
      required={required}
      value={value}
      searchPlaceholder={resolvedSearchPlaceholder}
      components={{
        option: CustomWorkspaceOption,
      }}
      onChange={handhandleWorkspaceSelectClick}
      disabled={disabled}
      showRequiredIndicator={showRequiredIndicator}
    />
  );
};

export default WorkspaceSelect;
